import { useMemo } from 'react';
import PropTypes from 'prop-types';

import SortChips from '..';
import FilterPlateCChecks from 'src/components/ui/FilterPlate/connectors/FilterPlateCChecks';
import { useFilters } from 'src/hooks/filters/useFilters';
import {
	formatActivityFilterChipValue,
	getActivityEntityTypeLabel,
	toActivityEntityTypeEnum,
} from 'src/utils/mappers/eventCard/activityFilters.mapper';

const ActivityChecksFilter = ({
	onClose,
	options,
	initialSelected,
	onApply,
	onReset,
}) => (
	<FilterPlateCChecks
		options={options}
		initialSelected={initialSelected}
		onSubmit={({ value }) => {
			onApply(value);
			onClose?.();
		}}
		onCancel={() => {
			onReset();
			onClose?.();
		}}
	/>
);

ActivityChecksFilter.propTypes = {
	onClose: PropTypes.func,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			label: PropTypes.node,
		}),
	),
	initialSelected: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	),
	onApply: PropTypes.func.isRequired,
	onReset: PropTypes.func.isRequired,
};

const SortChipsCActivities = ({ facets }) => {
	const { getFilter, setFilters, removeFilter, transformFilterValue } =
		useFilters();

	const createdByFilter = getFilter('createdBy');
	const typeFilter = getFilter('itemEntityType');

	const selectedAuthorIds = useMemo(
		() => (createdByFilter?.eq ?? []).map(String),
		[createdByFilter],
	);

	const selectedTypes = useMemo(
		() => (typeFilter?.eq ?? []).map(String),
		[typeFilter],
	);

	const authorOptions = useMemo(() => {
		return (facets?.creators ?? [])
			.filter((creator) => creator.count > 0 || creator.selected)
			.map((creator) => ({
				value: String(creator.id),
				label: creator.name,
			}));
	}, [facets]);

	const typeOptions = useMemo(() => {
		return (facets?.types ?? [])
			.filter((type) => type.count > 0 || type.selected)
			.map((type) => ({
				value: toActivityEntityTypeEnum(type.value),
				label: getActivityEntityTypeLabel(type.value),
			}));
	}, [facets]);

	const authorChipValue = useMemo(() => {
		const labels = authorOptions
			.filter((option) =>
				selectedAuthorIds.includes(String(option.value)),
			)
			.map((option) => option.label);

		return formatActivityFilterChipValue(labels);
	}, [authorOptions, selectedAuthorIds]);

	const typeChipValue = useMemo(() => {
		const labels = typeOptions
			.filter((option) => selectedTypes.includes(String(option.value)))
			.map((option) => option.label);

		return formatActivityFilterChipValue(labels);
	}, [typeOptions, selectedTypes]);

	const sorts = useMemo(() => {
		const applyFilter = (code, values) => {
			if (!values?.length) {
				removeFilter(code);
				return;
			}

			const stored = transformFilterValue({ value: values }, code, 'eq');
			setFilters(code, 'eq', stored);
		};

		return [
			{
				id: 'createdBy',
				label: 'Автор',
				value: authorChipValue,
				FilterRP: (props) => (
					<ActivityChecksFilter
						{...props}
						options={authorOptions}
						initialSelected={selectedAuthorIds}
						onApply={(value) => applyFilter('createdBy', value)}
						onReset={() => removeFilter('createdBy')}
					/>
				),
			},
			{
				id: 'itemEntityType',
				label: 'Тип',
				value: typeChipValue,
				FilterRP: (props) => (
					<ActivityChecksFilter
						{...props}
						options={typeOptions}
						initialSelected={selectedTypes}
						onApply={(value) =>
							applyFilter('itemEntityType', value)
						}
						onReset={() => removeFilter('itemEntityType')}
					/>
				),
			},
		];
	}, [
		authorChipValue,
		authorOptions,
		removeFilter,
		selectedAuthorIds,
		selectedTypes,
		setFilters,
		transformFilterValue,
		typeChipValue,
		typeOptions,
	]);

	return <SortChips sorts={sorts} />;
};

SortChipsCActivities.propTypes = {
	facets: PropTypes.shape({
		creators: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				name: PropTypes.string,
				count: PropTypes.number,
				selected: PropTypes.bool,
			}),
		),
		types: PropTypes.arrayOf(
			PropTypes.shape({
				value: PropTypes.oneOfType([
					PropTypes.string,
					PropTypes.object,
				]),
				count: PropTypes.number,
				selected: PropTypes.bool,
			}),
		),
	}),
};

export default SortChipsCActivities;
